// Aceite de termos, gate de `403 terms_not_accepted` (`spec/17-frontend.md` §8.1).
// Esta tela só chama rotas que rodam com `skipTerms`: `GET /v1/me/terms` (API-24) e
// `POST /v1/me/terms/accept`. Qualquer rota guardada devolveria o mesmo 403 aqui e a tela
// se redirecionaria em laço.
//
// A versão NÃO é mais constante no front: quem manda é `TERMS_VERSION` da API. Com a
// constante duplicada, subir a versão no servidor deixava o painel aceitando a antiga -
// o `POST` passava e o gate reprovava no request seguinte, em laço.

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { api } from '../api/client.ts';
import { errorMessageOf } from '../api/errors.ts';
import type { paths } from '../api/types/public.d.ts';
import { FormError } from '../components/app/auth-shell.tsx';
import { PageHeader } from '../components/app/page-header.tsx';
import { Button } from '../components/ui/button.tsx';
import { Card, CardContent } from '../components/ui/card.tsx';

type TermsResponse = paths['/v1/me/terms']['get']['responses'][200]['content']['application/json'];

function Terms() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const terms = useQuery({
    queryKey: ['terms'],
    queryFn: () => api<TermsResponse>('/v1/me/terms'),
    staleTime: 5 * 60_000,
  });
  const version = terms.data?.version;

  const accept = useMutation({
    mutationFn: async (accepted: string) =>
      api('/v1/me/terms/accept', { method: 'POST', body: { version: accepted } }),
    onSuccess: async () => {
      queryClient.clear();
      await navigate({ to: '/app' });
    },
  });

  return (
    <>
      <PageHeader
        title="Termos de uso"
        description={version === undefined ? undefined : `Versão ${version}`}
      />
      <Card>
        <CardContent className="flex flex-col gap-4">
          <p className="text-sm text-muted-foreground">
            {terms.data?.summary ??
              'Para usar o painel é preciso aceitar os termos de uso e a política de privacidade vigentes. O aceite fica registrado na sua conta com data e versão.'}
          </p>
          {terms.isError && <FormError message={errorMessageOf(terms.error)} />}
          {accept.isError && <FormError message={errorMessageOf(accept.error)} />}
          <Button
            className="self-start"
            type="button"
            onClick={() => {
              if (version !== undefined) {
                accept.mutate(version);
              }
            }}
            disabled={accept.isPending || version === undefined}
          >
            {accept.isPending ? 'Registrando…' : 'Aceitar e continuar'}
          </Button>
        </CardContent>
      </Card>
    </>
  );
}

export const Route = createFileRoute('/app/terms')({ component: Terms });
